"use client";

import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type ReactNode,
    type RefObject,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { toast } from "sonner";

import { useCommandSession } from "@/components/aios-command";
import { useSendMessage } from "@/components/aios-command/chat/use-send-message";
import { useMergedMessages } from "@/components/aios-command/chat/use-merged-messages";
import { useStreamReconnect } from "@/components/aios-command/chat/use-stream-reconnect";
import type { StreamEnd, StreamRecord } from "@/components/aios-command/chat/stream-supervisor";
import { MAX_GENERATIONS_PER_MESSAGE } from "@/components/aios-command/chat/chat-constants";
import {
    createEventHandler,
    processSSEChunk,
    supervisedChunkHandler,
} from "@/components/aios-command/chat/sse-event-handler";
import { ExtendedChatMessage } from "@/components/aios-command/chat/chat-types";
import {
    groupMessagesWithBranches,
    mapMessagesAssistantById,
    parseFetchedMessages,
} from "@/components/aios-command/chat/message-utils";
import {
    CommandPrototypeContext,
    compareSessions,
    type CommandProtoActions,
    type CommandPrototypeStoreResult,
} from "@/components/command-team-v2/prototype/store";
import {
    mapAgentToProto,
    mapMessageToProto,
    mapPresetToProto,
    mapProjectToProto,
    mapSessionToProto,
} from "@/components/command-team-v2/utils/map-api-to-proto";
import { readUrlSessionId } from "@/components/command-team-v2/utils/read-url-session-id";
import { useCommandAgents } from "@/hooks/use-command-agents";
import { useIntegrationsAppsLookup } from "@/hooks/use-integrations-apps-lookup";
import { refreshCommandMessagesPageOne, useCommandMessages } from "@/hooks/use-command-messages";
import { useCommandProjects } from "@/hooks/use-command-projects";
import { useCommandSessions } from "@/hooks/use-command-sessions";
import { useCommandV2AllSessions } from "@/hooks/use-command-v2-all-sessions";
import { useCommandV2ChatNavigation, buildCommandV2AgentHomePath, buildCommandV2ChatPath } from "@/hooks/use-command-v2-chat-navigation";
import { useCommandV2CreateSchedule } from "@/hooks/use-command-v2-create-schedule";
import { useCommandV2Presets } from "@/hooks/use-command-v2-presets";
import { useActivateCommandAgent } from "@/hooks/use-activate-command-agent";
import { useFileAttachmentQueue, type AttachmentItem } from "@/hooks/use-file-attachment-queue";
import type { CommandV2ComposerApi } from "@/components/command-team-v2/types";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { del, isGatewayError, patch, post, postStream, StreamIdleTimeoutError } from "@/utils/api-handler";
import type { CommandAgent, CommandSession, ContextUsage } from "@/types/command-agent";
import { pickLatestContextUsage } from "@/utils/command-context-usage";
import { resolveChatAgentId, resolveProjectAgentId } from "@/utils/command-project-agent";
import {
    protoAgentPatchToApi,
    protoAgentValuesToCreatePayload,
} from "@/components/command-team-v2/utils/proto-agent-api";
import type {
    CommandProtoState,
    ProtoAttachedFile,
    ProtoChatSession,
} from "@/components/command-team-v2/prototype/types";
import {
    CommandV2AgentSwitchDialog,
    type CommandV2AgentSwitchPrompt,
} from "@/components/command-team-v2/dialogs/command-v2-agent-switch-dialog";

interface CommandV2ApiProviderProps {
    children: ReactNode;
}

const CHAT_BASE = "/workforce/command-team/chat";

function isCommandV2ChatPath(pathname: string | null): boolean {
    if (!pathname) return false;
    return pathname === CHAT_BASE || pathname.startsWith(`${CHAT_BASE}/`);
}

export function CommandV2ApiProvider({ children }: CommandV2ApiProviderProps) {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const queryClient = useQueryClient();

    const {
        agentId,
        activeSessionId,
        activeSessionAgentId,
        handleSessionSelect,
        handleNewSessionCreated,
        handleSessionTitleUpdate,
    } = useCommandSession();

    const [draftProjectId, setDraftProjectId] = useState<string | null>(null);
    const [agentSwitchPrompt, setAgentSwitchPrompt] =
        useState<CommandV2AgentSwitchPrompt | null>(null);
    const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
    const [streamingPhase, setStreamingPhase] = useState<"working" | "responding">("working");
    const [contextUsage, setContextUsage] = useState<ContextUsage | null>(null);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(
        activeSessionId ?? searchParams.get("session_id")
    );

    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const streamAbortRef = useRef<Set<AbortController>>(new Set());
    const streamCancelledRef = useRef(false);
    const pendingMessagesRef = useRef<ExtendedChatMessage[]>([]);
    const stickyContextRef = useRef<any>(null);
    const prevSessionIdRef = useRef<string | null>(null);
    const isTypingRef = useRef(false);
    const streamingMessageIdRef = useRef<string | null>(null);
    const currentSessionIdRef = useRef<string | null>(null);
    const fetchedMessagesRef = useRef<ExtendedChatMessage[]>([]);
    const contextUsageRef = useRef<ContextUsage | null>(null);
    const contextUsageSessionRef = useRef<string | null>(null);
    const draftProjectIdRef = useRef<string | null>(null);
    const effectiveProjectIdRef = useRef<string | null>(null);
    /** Agent active before entering a project — restored when opening an unfiled chat. */
    const preProjectAgentIdRef = useRef<string | null>(null);
    const lastAgentRedirectRef = useRef<string | null>(null);
    const lastProjectRedirectRef = useRef<string | null>(null);
    /** Target session while router.push is catching up — survives stale URL Redux restore. */
    const pendingChatNavigationRef = useRef<string | null>(null);
    /** Suppresses stale session ids while switching agents — state so UI re-renders immediately. */
    const [pendingAgentSwitchId, setPendingAgentSwitchId] = useState<string | null>(null);
    const prevAgentIdRef = useRef<string | null>(agentId);
    const lastSyncedSessionKeyRef = useRef<string | null>(null);
    const prevOnChatRouteRef = useRef<boolean | null>(null);

    draftProjectIdRef.current = draftProjectId;

    const urlProjectId = searchParams.get("project_id");
    const urlSessionId = searchParams.get("session_id");
    const fallbackSessionId = activeSessionId ?? currentSessionId;
    const localSelectionAhead =
        Boolean(
            currentSessionId &&
            urlSessionId &&
            currentSessionId !== urlSessionId &&
            (pendingChatNavigationRef.current === currentSessionId ||
                activeSessionId === currentSessionId)
        );
    const staleReduxSession =
        Boolean(
            fallbackSessionId &&
            agentId &&
            activeSessionAgentId &&
            activeSessionAgentId !== agentId &&
            !localSelectionAhead
        );
    const resolvedSessionId = pendingAgentSwitchId
        ? null
        : localSelectionAhead
          ? currentSessionId
          : (urlSessionId ?? (staleReduxSession ? null : fallbackSessionId ?? null));
    const sessionIdForMessages = resolvedSessionId;
    currentSessionIdRef.current = sessionIdForMessages;

    useEffect(() => {
        if (
            pendingChatNavigationRef.current &&
            urlSessionId === pendingChatNavigationRef.current
        ) {
            pendingChatNavigationRef.current = null;
        }
    }, [urlSessionId]);

    const { allAgents, isLoadingAgents } = useCommandAgents();
    const selectedAgentId = useMemo(
        () => allAgents.find((item) => item.is_selected)?.id ?? agentId ?? null,
        [allAgents, agentId]
    );
    const activateAgentMutation = useActivateCommandAgent({
        onError: (variables) => {
            setPendingAgentSwitchId(null);
            if (variables.previousAgentId) {
                router.push(buildCommandV2ChatPath(variables.previousAgentId));
            }
        },
    });
    const { lookupItems } = useIntegrationsAppsLookup(allAgents.length > 0);
    const {
        allProjects,
        isLoadingProjects,
        createProject: createProjectMutation,
        updateProject: updateProjectMutation,
        deleteProject: deleteProjectMutation,
    } = useCommandProjects();

    const projectIds = useMemo(() => allProjects.map((project) => project.id), [allProjects]);

    const {
        allSessions,
        isLoadingUnfiled,
        isLoadingSessions,
        isFetchingSessions,
        hasNextPage: hasMoreSessions,
        isFetchingNextPage: isFetchingMoreSessions,
        loadMoreRef: sessionsLoadMoreRef,
    } = useCommandV2AllSessions({
        agentId: selectedAgentId,
        projectIds,
    });

    const isSwitchingAgent =
        Boolean(pendingAgentSwitchId) || activateAgentMutation.isPending;
    /** Other chats only — project folder sessions stay visible while switching agents. */
    const isLoadingUnfiledSessions =
        isSwitchingAgent || (Boolean(selectedAgentId) && isLoadingUnfiled);

    const sessionsForSidebar = useMemo(() => {
        const projectSessions = allSessions.filter((session) => session.project_id);
        if (isLoadingUnfiledSessions) {
            return projectSessions;
        }
        if (!selectedAgentId) return allSessions;
        const unfiledForAgent = allSessions.filter(
            (session) =>
                !session.project_id &&
                (!session.agent_id || session.agent_id === selectedAgentId)
        );
        return [...projectSessions, ...unfiledForAgent];
    }, [isLoadingUnfiledSessions, allSessions, selectedAgentId]);

    const navigateAgentId = agentId ?? "";
    const { navigateToSession, navigateToProject } = useCommandV2ChatNavigation(navigateAgentId);

    const activeSessionProjectId = useMemo(() => {
        if (!sessionIdForMessages) return null;
        const session = allSessions.find((item) => item.id === sessionIdForMessages);
        return session?.project_id ?? null;
    }, [sessionIdForMessages, allSessions]);

    /** Project context: explicit draft, URL param (project landing), or open session's project. */
    const effectiveProjectId = useMemo(() => {
        if (draftProjectId) return draftProjectId;
        // Restoring a session from URL — wait for the session row, not stale project_id.
        if (urlSessionId || resolvedSessionId) return activeSessionProjectId;
        if (urlProjectId) return urlProjectId;
        return null;
    }, [draftProjectId, urlSessionId, resolvedSessionId, urlProjectId, activeSessionProjectId]);

    const chatAgentId = useMemo(
        () =>
            resolveChatAgentId({
                urlAgentId: agentId,
                draftProjectId: effectiveProjectId,
                projects: allProjects,
                sessions: allSessions,
            }),
        [agentId, effectiveProjectId, allProjects, allSessions]
    );

    const sessionAgentId = chatAgentId ?? agentId;

    const activeSessionOwnerId = useMemo(() => {
        if (!sessionIdForMessages) return null;
        const session = allSessions.find((item) => item.id === sessionIdForMessages);
        if (session?.agent_id) return session.agent_id;
        if (session?.project_id) {
            return (
                resolveProjectAgentId(session.project_id, allProjects, allSessions) ??
                null
            );
        }
        return null;
    }, [sessionIdForMessages, allSessions, allProjects]);

    const messageAgentId =
        activeSessionOwnerId ??
        activeSessionAgentId ??
        sessionAgentId;

    effectiveProjectIdRef.current = effectiveProjectId;

    const onChatRoute = isCommandV2ChatPath(pathname);

    useEffect(() => {
        if (urlSessionId || activeSessionId) return;
        if (urlProjectId && draftProjectId !== urlProjectId) {
            setDraftProjectId(urlProjectId);
        }
    }, [urlProjectId, urlSessionId, activeSessionId, draftProjectId]);

    const {
        createSession,
        updateSession,
        deleteSession,
        allSessions: unfiledSessionsForStream,
        isCreatingSession,
    } = useCommandSessions(messageAgentId);

    const {
        allMessages: fetchedMessages,
        isPendingMessages,
        isLoadingMessages,
        isFetchingMessages,
        isFetchingNextPage,
        loadMoreRef,
        canListMessages,
    } = useCommandMessages(messageAgentId, sessionIdForMessages);

    const {
        presets,
        isLoadingPresets,
        toggleFavorite,
        createPreset,
        hasNextPage: hasMorePresets,
        isFetchingNextPage: isFetchingMorePresets,
        fetchNextPage: loadMorePresets,
    } = useCommandV2Presets();
    const { createFromDialog } = useCommandV2CreateSchedule();

    const invalidateAgentQueries = (agentId?: string) => {
        queryClient.invalidateQueries({ queryKey: ["command-agents-sidebar"] });
        queryClient.invalidateQueries({ queryKey: ["command-agents"] });
        if (agentId) {
            queryClient.invalidateQueries({ queryKey: ["command-agent", agentId] });
        }
    };

    const updateAgentMutation = useProtectedMutation({
        mutationFn: ({
            agentId: targetAgentId,
            payload,
        }: {
            agentId: string;
            payload: Record<string, unknown>;
        }) => patch(`/api/command-team/agents/${targetAgentId}`, payload),
        requiredPermissions: ["UPDATE_COMMAND_AGENT"],
        onSuccess: async (_response, { agentId: targetAgentId }) => {
            await Promise.all([
                queryClient.refetchQueries({ queryKey: ["command-agents"] }),
                queryClient.refetchQueries({ queryKey: ["command-agent", targetAgentId] }),
            ]);
            invalidateAgentQueries(targetAgentId);
        },
        onError: () => {
            toast.error("Failed to update agent");
        },
    });

    const createAgentMutation = useProtectedMutation({
        mutationFn: (payload: ReturnType<typeof protoAgentValuesToCreatePayload>) =>
            post("/api/command-team/agents", payload),
        requiredPermissions: ["CREATE_COMMAND_AGENT"],
        onSuccess: () => {
            invalidateAgentQueries();
        },
        onError: () => {
            toast.error("Failed to create agent");
        },
    });

    const deleteAgentMutation = useProtectedMutation({
        mutationFn: (targetAgentId: string) => del(`/api/command-team/agents/${targetAgentId}`),
        requiredPermissions: ["DELETE_COMMAND_AGENT"],
        onSuccess: (_response, targetAgentId) => {
            invalidateAgentQueries(targetAgentId);
        },
        onError: () => {
            toast.error("Failed to delete agent");
        },
    });

    const updateAgentMutationRef = useRef(updateAgentMutation);
    updateAgentMutationRef.current = updateAgentMutation;
    const createAgentMutationRef = useRef(createAgentMutation);
    createAgentMutationRef.current = createAgentMutation;
    const deleteAgentMutationRef = useRef(deleteAgentMutation);
    deleteAgentMutationRef.current = deleteAgentMutation;

    const {
        attachedFiles,
        clear: clearAttachments,
        fileInputRef,
        handleFileSelect,
        removeAttachedFile,
        isDraggingOver,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handlePaste,
    } = useFileAttachmentQueue();

    useMergedMessages({
        fetchedMessages,
        isLoadingMessages,
        isFetchingMessages,
        sessionId: sessionIdForMessages,
        isTyping,
        streamingMessageId,
        pendingMessagesRef,
        setMessages,
    });

    useEffect(() => {
        isTypingRef.current = isTyping;
    }, [isTyping]);

    useEffect(() => {
        streamingMessageIdRef.current = streamingMessageId;
    }, [streamingMessageId]);

    useEffect(() => {
        if (!pendingAgentSwitchId || !onChatRoute) return;
        if (agentId !== pendingAgentSwitchId) return;
        if (urlSessionId || urlProjectId) {
            router.replace(buildCommandV2AgentHomePath(pendingAgentSwitchId));
            return;
        }
        if (activeSessionId || currentSessionId) {
            setCurrentSessionId(null);
            handleSessionSelect(null);
            return;
        }
        setPendingAgentSwitchId(null);
    }, [
        pendingAgentSwitchId,
        onChatRoute,
        agentId,
        urlSessionId,
        urlProjectId,
        activeSessionId,
        currentSessionId,
        handleSessionSelect,
        router,
    ]);

    useEffect(() => {
        const prevAgentId = prevAgentIdRef.current;
        prevAgentIdRef.current = agentId;
        if (!agentId || prevAgentId === agentId || prevAgentId == null) return;
        // Cross-agent project chat navigation keeps session_id in the URL.
        if (urlSessionId) return;
        setCurrentSessionId(null);
        handleSessionSelect(null);
    }, [agentId, handleSessionSelect, urlSessionId]);

    useEffect(() => {
        if (pendingAgentSwitchId) return;
        if (localSelectionAhead) return;

        if (urlSessionId) {
            const session = allSessions.find((item) => item.id === urlSessionId);
            const ownerAgentId = session?.agent_id;
            const sessionsSettled = !isLoadingSessions && !isFetchingSessions;
            const sessionInProject =
                Boolean(session?.project_id) ||
                Boolean(urlProjectId) ||
                Boolean(preProjectAgentIdRef.current);
            if (
                agentId &&
                sessionsSettled &&
                ownerAgentId &&
                ownerAgentId !== agentId &&
                !sessionInProject
            ) {
                router.replace(buildCommandV2AgentHomePath(agentId));
                setCurrentSessionId(null);
                handleSessionSelect(null);
                return;
            }
            if (currentSessionId !== urlSessionId) {
                setCurrentSessionId(urlSessionId);
            }
            if (activeSessionId !== urlSessionId) {
                handleSessionSelect(urlSessionId, ownerAgentId ?? undefined);
            } else if (ownerAgentId && activeSessionAgentId !== ownerAgentId) {
                handleSessionSelect(urlSessionId, ownerAgentId);
            }
            return;
        }
        if (
            activeSessionId != null &&
            activeSessionId !== currentSessionId &&
            (!agentId || !activeSessionAgentId || activeSessionAgentId === agentId)
        ) {
            setCurrentSessionId(activeSessionId);
        }
    }, [
        pendingAgentSwitchId,
        agentId,
        activeSessionId,
        activeSessionAgentId,
        currentSessionId,
        urlSessionId,
        urlProjectId,
        localSelectionAhead,
        handleSessionSelect,
        allSessions,
        isLoadingSessions,
        isFetchingSessions,
        router,
    ]);

    useLayoutEffect(() => {
        if (pendingAgentSwitchId) return;
        if (localSelectionAhead) return;
        if (!onChatRoute || !sessionAgentId) return;
        if (preProjectAgentIdRef.current) return;
        if (
            resolvedSessionId &&
            activeSessionAgentId &&
            activeSessionAgentId !== sessionAgentId
        ) {
            const redirectKey = `${activeSessionAgentId}:${resolvedSessionId}:${activeSessionProjectId ?? ""}`;
            if (lastAgentRedirectRef.current === redirectKey) return;
            lastAgentRedirectRef.current = redirectKey;
            router.replace(
                buildCommandV2ChatPath(activeSessionAgentId, {
                    sessionId: resolvedSessionId,
                    projectId: activeSessionProjectId,
                })
            );
        }
    }, [
        onChatRoute,
        sessionAgentId,
        resolvedSessionId,
        activeSessionAgentId,
        activeSessionProjectId,
        localSelectionAhead,
        router,
    ]);

    useEffect(() => {
        if (pendingAgentSwitchId) return;
        if (!onChatRoute || !effectiveProjectId) return;
        if (preProjectAgentIdRef.current) return;
        const targetAgentId = resolveProjectAgentId(
            effectiveProjectId,
            allProjects,
            allSessions
        );
        if (!targetAgentId || targetAgentId === agentId) return;
        if (
            resolvedSessionId &&
            activeSessionAgentId &&
            activeSessionAgentId !== targetAgentId
        ) {
            return;
        }
        const redirectKey = `${targetAgentId}:${resolvedSessionId ?? ""}:${effectiveProjectId}`;
        if (lastProjectRedirectRef.current === redirectKey) return;
        lastProjectRedirectRef.current = redirectKey;
        router.replace(
            buildCommandV2ChatPath(targetAgentId, {
                projectId: !resolvedSessionId ? effectiveProjectId : undefined,
                sessionId: resolvedSessionId,
            })
        );
    }, [onChatRoute, effectiveProjectId, allProjects, allSessions, agentId, resolvedSessionId, activeSessionAgentId, router]);

    useLayoutEffect(() => {
        const previousSessionId = prevSessionIdRef.current;
        const newSessionId = sessionIdForMessages ?? null;

        if (previousSessionId !== newSessionId) {
            const isInitialHydration =
                previousSessionId === null && newSessionId !== null;
            const isCreatingNewSession = previousSessionId === null && newSessionId !== null;
            const hasPendingMessages = pendingMessagesRef.current.length > 0;

            if (
                !isInitialHydration &&
                !(isCreatingNewSession && hasPendingMessages)
            ) {
                streamCancelledRef.current = true;
                setMessages([]);
                pendingMessagesRef.current = [];
                setIsTyping(false);
                setStreamingMessageId(null);
                clearAttachments();
            }
        }

        prevSessionIdRef.current = newSessionId;
    }, [sessionIdForMessages, clearAttachments]);

    const syncOpenSessionThread = useCallback(
        (agentIdForMessages: string, sessionId: string) => {
            void refreshCommandMessagesPageOne(queryClient, agentIdForMessages, sessionId);
            void queryClient.invalidateQueries({ queryKey: ["command-sessions"] });
            void queryClient.invalidateQueries({ queryKey: ["command-project-sessions-page"] });
        },
        [queryClient]
    );

    useEffect(() => {
        if (!sessionIdForMessages || !messageAgentId) return;
        const sendingIntoThisSession =
            pendingMessagesRef.current.length > 0 &&
            (isTypingRef.current || Boolean(streamingMessageIdRef.current));
        const key = `${messageAgentId}:${sessionIdForMessages}`;
        if (sendingIntoThisSession) {
            lastSyncedSessionKeyRef.current = key;
            return;
        }
        if (lastSyncedSessionKeyRef.current === key) return;
        lastSyncedSessionKeyRef.current = key;
        syncOpenSessionThread(messageAgentId, sessionIdForMessages);
    }, [sessionIdForMessages, messageAgentId, syncOpenSessionThread]);

    useEffect(() => {
        const wasOnChatRoute = prevOnChatRouteRef.current;
        prevOnChatRouteRef.current = onChatRoute;
        if (!onChatRoute || wasOnChatRoute !== false) return;
        if (!sessionIdForMessages || !messageAgentId) return;
        const sendingIntoThisSession =
            pendingMessagesRef.current.length > 0 &&
            (isTypingRef.current || Boolean(streamingMessageIdRef.current));
        if (sendingIntoThisSession) return;
        lastSyncedSessionKeyRef.current = `${messageAgentId}:${sessionIdForMessages}`;
        syncOpenSessionThread(messageAgentId, sessionIdForMessages);
    }, [onChatRoute, sessionIdForMessages, messageAgentId, syncOpenSessionThread]);

    fetchedMessagesRef.current = fetchedMessages;

    const handleContextUsage = useCallback((usage: ContextUsage) => {
        contextUsageRef.current = usage;
        setContextUsage(usage);
    }, []);

    const sessionStreamActive = useMemo(() => {
        if (!sessionIdForMessages) return false;
        const row =
            unfiledSessionsForStream.find((session) => session.id === sessionIdForMessages) ??
            allSessions.find((session) => session.id === sessionIdForMessages);
        return row?.status?.toUpperCase() === "GENERATING";
    }, [unfiledSessionsForStream, allSessions, sessionIdForMessages]);

    // Only a row the server itself reports GENERATING. When just the session row says so,
    // useStreamReconnect re-reads page 1 until the run's own row shows up.
    const generatingAssistantId = useMemo(() => {
        for (let index = fetchedMessages.length - 1; index >= 0; index -= 1) {
            const message = fetchedMessages[index];
            if (
                message.role === "assistant" &&
                (message as { status?: string }).status === "GENERATING"
            ) {
                return message.id;
            }
        }
        return null;
    }, [fetchedMessages]);

    const streamSupervisor = useStreamReconnect({
        agentId: messageAgentId,
        sessionId: sessionIdForMessages,
        sessionStreamActive,
        generatingAssistantId,
        isLoadingMessages,
        fetchedMessagesRef,
        isTypingRef,
        streamingMessageIdRef,
        isTyping,
        streamingMessageId,
        streamAbortRef,
        setMessages,
        setIsTyping,
        setStreamingMessageId,
        setStreamingPhase,
        onSessionTitle: handleSessionTitleUpdate,
        queryClient,
        onContextUsage: handleContextUsage,
    });

    const createSessionMutation = useMemo(
        () => ({
            mutate: (
                _vars: undefined,
                opts: {
                    onSuccess: (data: CommandSession) => void;
                    onError: () => void;
                }
            ) => {
                const projectId = effectiveProjectIdRef.current;
                const ownerAgentId = messageAgentId ?? agentId;
                createSession(
                    {
                        ...(projectId ? { project_id: projectId } : {}),
                        silent: true,
                    },
                    {
                        onSuccess: (session) => {
                            handleNewSessionCreated(session.id, ownerAgentId ?? undefined);
                            setDraftProjectId(null);
                            if (ownerAgentId) {
                                router.replace(
                                    buildCommandV2ChatPath(ownerAgentId, {
                                        sessionId: session.id,
                                        projectId:
                                            projectId ?? session.project_id ?? undefined,
                                    })
                                );
                            }
                            opts.onSuccess(session);
                        },
                        onError: opts.onError,
                    }
                );
            },
        }),
        [createSession, handleNewSessionCreated, agentId, messageAgentId, router]
    );

    useEffect(() => {
        if (pendingAgentSwitchId) return;
        if (localSelectionAhead) return;
        if (!onChatRoute || !messageAgentId) return;

        const urlSession = readUrlSessionId(searchParams);
        const urlProject = searchParams.get("project_id");
        const expectedProject = activeSessionProjectId;
        const pathAgentId =
            preProjectAgentIdRef.current ?? messageAgentId ?? agentId;
        if (!pathAgentId) return;

        // When URL already has a session, only fix project_id — never swap session_id.
        if (urlSession) {
            if (currentSessionId && urlSession !== currentSessionId) return;
            const projectMatches = (urlProject ?? null) === (expectedProject ?? null);
            if (projectMatches) return;
            router.replace(
                buildCommandV2ChatPath(pathAgentId, {
                    sessionId: urlSession,
                    projectId: expectedProject,
                })
            );
            return;
        }

        // No session in URL yet — add the locally selected session (in-app navigation).
        if (!currentSessionId) return;
        const sessionOwner = allSessions.find(
            (session) => session.id === currentSessionId
        )?.agent_id;
        if (agentId && sessionOwner && sessionOwner !== agentId) return;
        if (agentId && activeSessionAgentId && activeSessionAgentId !== agentId) return;
        router.replace(
            buildCommandV2ChatPath(pathAgentId, {
                sessionId: currentSessionId,
                projectId: expectedProject,
            })
        );
    }, [
        pendingAgentSwitchId,
        localSelectionAhead,
        onChatRoute,
        currentSessionId,
        router,
        searchParams,
        messageAgentId,
        agentId,
        activeSessionAgentId,
        activeSessionProjectId,
        allSessions,
    ]);

    const {
        sendMessage: sendMessageApi,
        handleStopGeneration,
        isStopping,
    } = useSendMessage({
        agentId: messageAgentId,
        currentSessionId: sessionIdForMessages,
        isTyping,
        streamingMessageId,
        attachedFiles,
        createSessionMutation,
        clearAttachments,
        setInputValue: () => undefined,
        setCurrentSessionId,
        setMessages,
        setIsTyping,
        setStreamingMessageId,
        setStreamingPhase,
        streamCancelledRef,
        streamAbortRef,
        pendingMessagesRef,
        stickyContextRef,
        inputRef: textareaRef,
        onSessionTitle: handleSessionTitleUpdate,
        queryClient,
        lastContextUsageRef: contextUsageRef,
        onContextUsage: handleContextUsage,
        streamSupervisor,
    });

    const sendMessageRef = useRef(sendMessageApi);
    sendMessageRef.current = sendMessageApi;
    const createSessionRef = useRef(createSession);
    createSessionRef.current = createSession;
    const isCreatingSessionRef = useRef(isCreatingSession);
    isCreatingSessionRef.current = isCreatingSession;

    const regenerateMessageMutation = useProtectedMutation({
        mutationFn: async ({
            messageId,
            sessionId,
            onEvent,
            record,
        }: {
            messageId: string;
            sessionId: string;
            onEvent: Parameters<typeof processSSEChunk>[1];
            record: StreamRecord;
        }) => {
            streamAbortRef.current.add(record.controller);
            let end: StreamEnd;
            try {
                end = await postStream(
                    `/api/command-team/agents/${messageAgentId}/sessions/${sessionId}/messages/${messageId}/regenerate`,
                    {},
                    supervisedChunkHandler(streamSupervisor, record, onEvent),
                    record.controller.signal,
                    () => streamSupervisor.noteActivity(record)
                );
            } catch (error) {
                if (error instanceof StreamIdleTimeoutError) {
                    end = { reason: "idle_timeout", responseStarted: true };
                } else if (isGatewayError(error)) {
                    // The proxy lost the backend before it answered; the run may have started anyway.
                    record.target.failureMessage = error instanceof Error ? error.message : undefined;
                    end = { reason: "network_error", responseStarted: false };
                } else {
                    streamSupervisor.abandon(record);
                    throw error;
                }
            } finally {
                streamAbortRef.current.delete(record.controller);
            }
            return { success: true, verdict: streamSupervisor.end(record, end), closedCleanly: end.reason === "eof" };
        },
        requiredPermissions: ["REGENERATE_COMMAND_MESSAGE"],
        onError: (error: unknown, { record }) => {
            const err = error as { name?: string; message?: string; response?: { data?: { message?: string } } };
            const isAborted =
                err?.name === "AbortError" ||
                err?.message?.includes("aborted") ||
                streamCancelledRef.current;
            if (isAborted) return;
            toast.error("Failed to regenerate message", {
                description:
                    err.response?.data?.message ||
                    err.message ||
                    "An unexpected error occurred",
            });
            // A newer stream may already hold the lock; only this run's branch is cleaned up then.
            if (!streamSupervisor.releaseLock(record)) {
                setMessages((prev) =>
                    mapMessagesAssistantById(prev, record.target.assistantId, (branch) => ({
                        ...branch,
                        isStreaming: false,
                    }))
                );
                return;
            }
            setIsTyping(false);
            setStreamingMessageId(null);
            setMessages((prev) =>
                prev.map((msg) => {
                    if (msg.branches) {
                        return {
                            ...msg,
                            branches: msg.branches.map((branch) =>
                                branch.isStreaming ? { ...branch, isStreaming: false } : branch
                            ),
                        };
                    }
                    return msg.isStreaming ? { ...msg, isStreaming: false } : msg;
                })
            );
        },
    });

    const regenerateMessage = useCallback(
        (messageId: string) => {
            const currentSessionId = sessionIdForMessages;
            if (!currentSessionId || !messageAgentId || isTyping) return;

            const originalMessage = messages.find((message) => message.id === messageId);
            if (!originalMessage || originalMessage.role !== "assistant") return;

            const generationCount = originalMessage.branches?.length ?? 1;
            if (generationCount >= MAX_GENERATIONS_PER_MESSAGE) {
                toast.error("Maximum regenerations reached", {
                    description: `You can generate up to ${MAX_GENERATIONS_PER_MESSAGE} responses per message`,
                });
                return;
            }

            const dbMessageId =
                originalMessage.dbId ||
                (messageId.startsWith("temp-") ? null : messageId);

            if (!dbMessageId) {
                toast.error("Cannot regenerate", {
                    description: "Please wait for the message to be saved before regenerating",
                });
                return;
            }

            const tempBranchId = `temp-regen-${Date.now()}`;
            const newBranchMessage: ExtendedChatMessage = {
                id: tempBranchId,
                content: "",
                role: "assistant",
                timestamp: new Date(),
                isStreaming: true,
                parentMessageId: messageId,
                ...(contextUsageRef.current
                    ? { contextUsage: contextUsageRef.current }
                    : {}),
            };

            streamCancelledRef.current = false;
            setStreamingPhase("working");
            const record = streamSupervisor.begin("regenerate", {
                agentId: messageAgentId,
                sessionId: currentSessionId,
                assistantId: tempBranchId,
                startedAt: Date.now(),
            });
            const eventHandler = createEventHandler({
                systemMessageId: tempBranchId,
                setMessages,
                onSessionTitle: handleSessionTitleUpdate,
                cancelledRef: record.cancelledRef,
                setStreamingPhase,
                onAssistantIdResolved: (realId) => streamSupervisor.noteAssistantId(record, realId),
                onContextUsage: handleContextUsage,
            });

            setMessages((prev) =>
                prev.map((msg) => {
                    if (msg.id !== messageId) return msg;
                    const existingBranches = msg.branches || [msg];
                    return {
                        ...msg,
                        branches: [...existingBranches, newBranchMessage],
                        activeBranchIndex: existingBranches.length,
                    };
                })
            );

            setStreamingMessageId(tempBranchId);
            setIsTyping(true);

            regenerateMessageMutation.mutate(
                {
                    messageId: dbMessageId,
                    sessionId: currentSessionId,
                    onEvent: eventHandler,
                    record,
                },
                {
                    onSuccess: async ({ verdict, closedCleanly }) => {
                        // Interrupted runs are being recovered; released ones belong to a newer stream.
                        if (verdict !== "completed") return;
                        setMessages((prev) =>
                            prev.map((msg) => {
                                if (msg.id !== messageId || !msg.branches) return msg;
                                return {
                                    ...msg,
                                    branches: msg.branches.map((branch) =>
                                        branch.isStreaming
                                            ? { ...branch, isStreaming: false }
                                            : branch
                                    ),
                                };
                            })
                        );

                        await queryClient.refetchQueries({
                            queryKey: [
                                "command-messages",
                                messageAgentId,
                                currentSessionId,
                            ],
                        });

                        // The backend can still report the new branch GENERATING right after it finished.
                        streamSupervisor.syncAfterCompletion(record.target, closedCleanly);
                        if (streamSupervisor.releaseLock(record)) {
                            setIsTyping(false);
                            setStreamingMessageId(null);
                        }
                    },
                }
            );
        },
        [
            sessionIdForMessages,
            isTyping,
            messages,
            messageAgentId,
            handleSessionTitleUpdate,
            regenerateMessageMutation,
            queryClient,
            streamSupervisor,
        ]
    );

    const getAssistantBranchCount = useCallback(
        (messageId: string) => {
            const message = messages.find((item) => item.id === messageId);
            if (!message || message.role !== "assistant") return 1;
            return message.branches?.length ?? 1;
        },
        [messages]
    );

    const changeMessageBranch = useCallback((messageId: string, branchIndex: number) => {
        setMessages((prev) =>
            prev.map((message) =>
                message.id === messageId
                    ? { ...message, activeBranchIndex: branchIndex }
                    : message
            )
        );
    }, []);

    const threadMessages = useMemo((): ExtendedChatMessage[] => {
        if (messages.length > 0) return messages;
        if (fetchedMessages.length > 0) {
            return groupMessagesWithBranches(parseFetchedMessages(fetchedMessages));
        }
        return messages;
    }, [messages, fetchedMessages]);

    useEffect(() => {
        const sessionKey = sessionIdForMessages ?? null;
        if (contextUsageSessionRef.current !== sessionKey) {
            contextUsageSessionRef.current = sessionKey;
            const initial = pickLatestContextUsage(threadMessages);
            contextUsageRef.current = initial;
            setContextUsage(initial);
            return;
        }
        const next = pickLatestContextUsage(threadMessages);
        if (next) {
            contextUsageRef.current = next;
            setContextUsage(next);
        }
    }, [sessionIdForMessages, threadMessages]);

    const protoMessages = useMemo(() => {
        const source =
            messages.length > 0
                ? messages
                : fetchedMessages.length > 0
                  ? groupMessagesWithBranches(parseFetchedMessages(fetchedMessages))
                  : messages;
        return source.map((message) => mapMessageToProto(message));
    }, [messages, fetchedMessages]);

    const sessionRowLoaded = useMemo(() => {
        if (!sessionIdForMessages) return false;
        return allSessions.some((session) => session.id === sessionIdForMessages);
    }, [sessionIdForMessages, allSessions]);

    const isLoadingThread =
        Boolean(sessionIdForMessages) &&
        !pendingAgentSwitchId &&
        threadMessages.length === 0 &&
        !isTyping &&
        !streamingMessageId &&
        (Boolean(urlSessionId) || sessionRowLoaded) &&
        (!canListMessages ||
            isPendingMessages ||
            isLoadingMessages ||
            isFetchingMessages ||
            !messageAgentId ||
            (!sessionRowLoaded && (isLoadingSessions || isFetchingSessions)));

    const protoSessions = useMemo(() => {
        return sessionsForSidebar.map((session) => {
            const isActive = session.id === sessionIdForMessages;
            return mapSessionToProto(session, isActive ? protoMessages : []);
        });
    }, [sessionsForSidebar, sessionIdForMessages, protoMessages]);

    const state = useMemo((): CommandProtoState => {
        return {
            presets: presets.map(mapPresetToProto),
            projects: allProjects.map(mapProjectToProto),
            sessions: [...protoSessions].sort(compareSessions),
            agents: allAgents.map((item) =>
                mapAgentToProto(item, { integrationLookup: lookupItems })
            ),
            activeSessionId: resolvedSessionId ?? null,
            draftProjectId: !resolvedSessionId ? effectiveProjectId : null,
            activeAgentId: selectedAgentId ?? null,
        };
    }, [
        presets,
        allProjects,
        protoSessions,
        allAgents,
        lookupItems,
        resolvedSessionId,
        effectiveProjectId,
        selectedAgentId,
    ]);

    const stateRef = useRef(state);
    stateRef.current = state;

    const loading = useMemo(
        () => ({
            agents: isLoadingAgents && allAgents.length === 0,
            projects: isLoadingProjects,
            sessions: isLoadingUnfiledSessions,
            presets: isLoadingPresets,
        }),
        [
            isLoadingAgents,
            allAgents.length,
            isLoadingProjects,
            isLoadingUnfiledSessions,
            isLoadingPresets,
        ]
    );

    const applyChatSelection = useCallback(
        (
            sessionId: string,
            ownerAgentId: string | null | undefined,
            projectId: string | null
        ) => {
            pendingChatNavigationRef.current = sessionId;
            setPendingAgentSwitchId(null);
            let messageAgent = ownerAgentId ?? agentId;
            let urlAgent = agentId;

            if (projectId) {
                if (
                    agentId &&
                    !activeSessionProjectId &&
                    !preProjectAgentIdRef.current
                ) {
                    preProjectAgentIdRef.current = agentId;
                }
                urlAgent = preProjectAgentIdRef.current ?? agentId ?? messageAgent;
            } else if (preProjectAgentIdRef.current) {
                urlAgent = preProjectAgentIdRef.current;
                preProjectAgentIdRef.current = null;
            } else if (!projectId && messageAgent && agentId && messageAgent !== agentId) {
                urlAgent = messageAgent;
            }

            if (
                !projectId &&
                messageAgent &&
                urlAgent &&
                messageAgent !== urlAgent
            ) {
                urlAgent = messageAgent;
            }

            setDraftProjectId(projectId);
            setCurrentSessionId(sessionId);
            navigateToSession(sessionId, {
                urlAgentId: urlAgent,
                messageAgentId: messageAgent,
                projectId,
            });
        },
        [agentId, activeSessionProjectId, navigateToSession]
    );

    const resolveSessionOwnerAgentId = useCallback(
        (session: CommandSession | undefined, projectId: string | null) => {
            if (session?.agent_id) return session.agent_id;
            if (projectId) {
                return (
                    resolveProjectAgentId(projectId, allProjects, allSessions) ??
                    agentId ??
                    null
                );
            }
            return selectedAgentId ?? agentId ?? null;
        },
        [allProjects, allSessions, agentId, selectedAgentId]
    );

    const actions = useMemo(
        (): CommandProtoActions => ({
            reset: () => {
                queryClient.invalidateQueries({ queryKey: ["command-sessions"] });
                queryClient.invalidateQueries({ queryKey: ["command-projects"] });
                queryClient.invalidateQueries({ queryKey: ["command-messages"] });
            },
            newChat: (projectId?: string | null) => {
                const targetProjectId = projectId ?? null;
                const restoredAgentId = !targetProjectId
                    ? preProjectAgentIdRef.current
                    : null;
                const targetAgentId =
                    resolveChatAgentId({
                        urlAgentId: restoredAgentId ?? agentId,
                        draftProjectId: targetProjectId,
                        projects: allProjects,
                        sessions: allSessions,
                    }) ?? restoredAgentId ?? agentId;
                if (!targetProjectId) {
                    preProjectAgentIdRef.current = null;
                }

                if (!targetAgentId) {
                    toast.error("No agent selected", {
                        description: "Pick a Command agent before starting a chat.",
                    });
                    return;
                }
                if (isCreatingSessionRef.current) return;

                setDraftProjectId(targetProjectId);
                setMessages([]);
                pendingMessagesRef.current = [];
                setIsTyping(false);
                setStreamingMessageId(null);
                clearAttachments();

                const finishNewSession = (session: CommandSession) => {
                    handleNewSessionCreated(session.id, targetAgentId);
                    setCurrentSessionId(session.id);
                    setDraftProjectId(null);
                    const urlAgent =
                        preProjectAgentIdRef.current ?? agentId ?? targetAgentId;
                    navigateToSession(session.id, {
                        urlAgentId: urlAgent,
                        messageAgentId: targetAgentId,
                        projectId: targetProjectId,
                    });
                };

                if (targetProjectId && agentId && !preProjectAgentIdRef.current) {
                    preProjectAgentIdRef.current = agentId;
                }

                if (targetAgentId !== agentId && !targetProjectId) {
                    router.push(buildCommandV2ChatPath(targetAgentId));
                }

                if (targetAgentId !== sessionAgentId) {
                    void (async () => {
                        try {
                            const body = targetProjectId
                                ? { project_id: targetProjectId }
                                : {};
                            const response = await post(
                                `/api/command-team/agents/${targetAgentId}/sessions`,
                                body
                            );
                            finishNewSession(response.data as CommandSession);
                        } catch {
                            toast.error("Failed to create session", {
                                description: "We couldn't start a new chat. Please try again.",
                            });
                        }
                    })();
                    return;
                }

                createSessionRef.current(
                    targetProjectId ? { project_id: targetProjectId } : undefined,
                    {
                        onSuccess: finishNewSession,
                    }
                );
            },
            selectChat: (sessionId: string) => {
                const session = allSessions.find((item) => item.id === sessionId);
                const projectId = session?.project_id ?? null;
                const sessionOwnerId = resolveSessionOwnerAgentId(session, projectId);
                const sameProjectContinuation =
                    Boolean(projectId) &&
                    Boolean(activeSessionProjectId) &&
                    projectId === activeSessionProjectId;
                const homeAgentId = preProjectAgentIdRef.current;

                if (homeAgentId && !sameProjectContinuation) {
                    applyChatSelection(
                        sessionId,
                        sessionOwnerId ?? homeAgentId,
                        projectId
                    );
                    return;
                }

                if (projectId && sessionOwnerId) {
                    applyChatSelection(sessionId, sessionOwnerId, projectId);
                    return;
                }

                // Ungrouped (Other chats): open directly — no agent-switch dialog.
                applyChatSelection(
                    sessionId,
                    sessionOwnerId ?? selectedAgentId ?? agentId,
                    null
                );
            },
            sendMessage: (
                text: string,
                options?: {
                    presetId?: string;
                    chainStepIndex?: number;
                    attachedFiles?: ProtoAttachedFile[];
                    sessionId?: string | null;
                    selectSession?: boolean;
                    projectId?: string | null;
                    selection_value?: string;
                    selection_tool_call_id?: string;
                }
            ): string | null => {
                const trimmed = text.trim();
                const hasReadyAttachments = attachedFiles.some(
                    (item) => item.status === "done" && item.uploaded
                );
                const isUploadingAttachments = attachedFiles.some(
                    (item) => item.status === "uploading" || item.status === "pending"
                );
                const hasSelection = Boolean(options?.selection_value);
                if (
                    (!trimmed && !hasReadyAttachments && !hasSelection) ||
                    isUploadingAttachments
                ) {
                    return null;
                }
                if (options?.projectId !== undefined) {
                    setDraftProjectId(options.projectId);
                }
                sendMessageRef.current(
                    trimmed,
                    options?.selection_value
                        ? {
                              selection_value: options.selection_value,
                              selection_tool_call_id: options.selection_tool_call_id,
                          }
                        : undefined
                );
                return sessionIdForMessages ?? null;
            },
            scheduleChat: (input: {
                prompt: string;
                title: string;
                projectId: string | null;
                cadenceLabel?: string;
                time?: string;
            }) => {
                createFromDialog({
                    prompt: input.prompt,
                    description: input.title,
                    cadenceLabel: input.cadenceLabel ?? "daily",
                    time: input.time ?? "08:00",
                });
            },
            runScheduleNow: (_anchorId: string) => null,
            renameChat: (sessionId: string, title: string) => {
                updateSession({ sessionId, title, ownerAgentId: agentId });
            },
            markChatRead: (_sessionId: string) => undefined,
            togglePinChat: (sessionId: string) => {
                const session = allSessions.find((item) => item.id === sessionId);
                updateSession({
                    sessionId,
                    pinned: !session?.pinned,
                    ownerAgentId: session?.agent_id ?? agentId,
                });
            },
            deleteChat: (sessionId: string) => {
                const session = allSessions.find((item) => item.id === sessionId);
                deleteSession(
                    session?.agent_id
                        ? { sessionId, ownerAgentId: session.agent_id }
                        : sessionId,
                    {
                        onSuccess: () => {
                            if (activeSessionId === sessionId) {
                                navigateToSession(null);
                            }
                        },
                    }
                );
            },
            deleteChats: (sessionIds: string[]) => {
                sessionIds.forEach((sessionId) => {
                    const session = allSessions.find((item) => item.id === sessionId);
                    deleteSession(
                        session?.agent_id
                            ? { sessionId, ownerAgentId: session.agent_id }
                            : sessionId
                    );
                });
            },
            moveChatToProject: (sessionId: string, projectId: string | null) => {
                const session = allSessions.find((item) => item.id === sessionId);
                updateSession({
                    sessionId,
                    project_id: projectId,
                    ownerAgentId: session?.agent_id ?? agentId,
                });
            },
            createProject: (name: string) => {
                createProjectMutation({
                    name,
                    visibility: "PERSONAL",
                    agent_id: agentId ?? null,
                });
                return "";
            },
            selectProject: (projectId: string) => {
                if (
                    agentId &&
                    !activeSessionProjectId &&
                    !preProjectAgentIdRef.current
                ) {
                    preProjectAgentIdRef.current = agentId;
                }
                const urlAgent = preProjectAgentIdRef.current ?? agentId;
                setDraftProjectId(projectId);
                setCurrentSessionId(null);
                navigateToProject(projectId, urlAgent ?? undefined);
            },
            selectAgent: (nextAgentId: string) => {
                if (!nextAgentId || activateAgentMutation.isPending) return;

                const backendActiveId =
                    allAgents.find((item) => item.is_selected)?.id ?? null;
                if (nextAgentId === agentId && backendActiveId === nextAgentId) {
                    return;
                }

                setPendingAgentSwitchId(nextAgentId);
                lastAgentRedirectRef.current = null;
                lastProjectRedirectRef.current = null;
                preProjectAgentIdRef.current = null;

                const cleanPath = buildCommandV2AgentHomePath(nextAgentId);
                if (typeof window !== "undefined") {
                    window.history.replaceState(null, "", cleanPath);
                }
                router.replace(cleanPath);

                setDraftProjectId(null);
                setMessages([]);
                pendingMessagesRef.current = [];
                setIsTyping(false);
                setStreamingMessageId(null);
                clearAttachments();
                setCurrentSessionId(null);
                prevSessionIdRef.current = null;
                handleSessionSelect(null);

                const selectedAgent = allAgents.find((item) => item.id === nextAgentId);
                activateAgentMutation.mutate({
                    agentId: nextAgentId,
                    name: selectedAgent?.name,
                    previousAgentId: backendActiveId ?? agentId,
                });
            },
            renameProject: (projectId: string, name: string) => {
                updateProjectMutation({ projectId, payload: { name } });
            },
            updateProject: (
                projectId: string,
                patch: Partial<{ name: string; description?: string; instructions?: string }>
            ) => {
                updateProjectMutation({ projectId, payload: patch });
            },
            deleteProjects: (projectIds: string[]) => {
                projectIds.forEach((projectId) => deleteProjectMutation(projectId));
            },
            reorderProject: (_projectId: string, _targetProjectId: string) => undefined,
            reorderAgent: (_agentId: string, _targetAgentId: string) => undefined,
            setAgentOrder: (_agentIds: string[]) => undefined,
            toggleFavourite: (presetId: string) => toggleFavorite(presetId),
            movePreset: (_presetId: string, _direction: "up" | "down") => undefined,
            reorderPreset: (_presetId: string, _targetPresetId: string) => undefined,
            createPreset,
            updatePreset: () => undefined,
            deletePreset: () => undefined,
            createCommandAgent: (values) => {
                createAgentMutationRef.current.mutate(protoAgentValuesToCreatePayload(values));
                return "";
            },
            updateCommandAgent: (targetAgentId, patchValues) => {
                updateAgentMutationRef.current.mutate({
                    agentId: targetAgentId,
                    payload: protoAgentPatchToApi(patchValues),
                });
            },
            toggleCommandAgentActive: () => undefined,
            deleteCommandAgent: (targetAgentId) => {
                deleteAgentMutationRef.current.mutate(targetAgentId);
            },
            updateChatSchedule: () => undefined,
        }),
        [
            agentId,
            messageAgentId,
            sessionAgentId,
            activeSessionId,
            activeSessionProjectId,
            allProjects,
            allAgents,
            allSessions,
            clearAttachments,
            createFromDialog,
            createProjectMutation,
            deleteProjectMutation,
            deleteSession,
            handleSessionSelect,
            handleNewSessionCreated,
            navigateToSession,
            navigateToProject,
            applyChatSelection,
            resolveSessionOwnerAgentId,
            queryClient,
            router,
            selectedAgentId,
            sessionIdForMessages,
            attachedFiles,
            toggleFavorite,
            createPreset,
            updateProjectMutation,
            updateSession,
            activateAgentMutation,
        ]
    );

    const composer = useMemo(
        (): CommandV2ComposerApi => ({
            textareaRef,
            fileInputRef,
            attachedFiles,
            handleFileSelect,
            removeAttachedFile,
            clearAttachments,
            isCreatingSession,
            isDraggingOver,
            handleDragOver,
            handleDragLeave,
            handleDrop,
            handlePaste,
        }),
        [
            attachedFiles,
            handleFileSelect,
            removeAttachedFile,
            clearAttachments,
            isDraggingOver,
            handleDragOver,
            handleDragLeave,
            handleDrop,
            handlePaste,
            isCreatingSession,
        ]
    );

    const activeSession = useMemo((): ProtoChatSession | null => {
        if (sessionIdForMessages) {
            const session = protoSessions.find(
                (item) => item.id === sessionIdForMessages
            );
            if (session) return session;

            const cached = allSessions.find((item) => item.id === sessionIdForMessages);
            const now = new Date().toISOString();
            return {
                id: sessionIdForMessages,
                title: cached?.title?.trim() || "Loading chat…",
                projectId: cached?.project_id ?? effectiveProjectId,
                messages: protoMessages,
                createdAt: cached?.created_at ?? now,
                updatedAt: cached?.updated_at ?? now,
                agentId: cached?.agent_id ?? sessionAgentId ?? null,
            };
        }

        if (messages.length > 0 || isTyping) {
            const now = new Date().toISOString();
            return {
                id: sessionIdForMessages ?? "pending",
                title: "New chat",
                projectId: effectiveProjectId,
                messages: protoMessages,
                createdAt: now,
                updatedAt: now,
                agentId: sessionAgentId ?? null,
            };
        }

        return null;
    }, [
        sessionIdForMessages,
        protoSessions,
        allSessions,
        messages.length,
        isTyping,
        protoMessages,
        effectiveProjectId,
        sessionAgentId,
    ]);

    const apiStore = useMemo(
        (): CommandPrototypeStoreResult => ({
            state,
            actions,
            activeSession,
            hydrated: true,
            loading,
            activatingAgentId: activateAgentMutation.isPending
                ? (activateAgentMutation.variables?.agentId ?? null)
                : null,
            isLoadingThread,
            loadMoreRef,
            isFetchingNextPage,
            sessionsLoadMoreRef,
            hasMoreSessions,
            isFetchingMoreSessions,
            hasMorePresets,
            isFetchingMorePresets,
            loadMorePresets,
            composer,
            regenerateMessage,
            // Follows the lock, not the request: a stalled regenerate POST can stay open after recovery finished it.
            isRegenerating: isTyping && Boolean(streamingMessageId?.startsWith("temp-regen-")),
            getAssistantBranchCount,
            threadMessages,
            changeMessageBranch,
            sessionIdForMessages: resolvedSessionId ?? null,
            handleStopGeneration,
            isStopping,
            isTyping,
            streamingMessageId,
            streamingPhase,
            stickyContextRef,
            contextUsage,
        }),
        [
            state,
            actions,
            activeSession,
            loading,
            activateAgentMutation.isPending,
            activateAgentMutation.variables?.agentId,
            isLoadingThread,
            loadMoreRef,
            isFetchingNextPage,
            sessionsLoadMoreRef,
            hasMoreSessions,
            isFetchingMoreSessions,
            hasMorePresets,
            isFetchingMorePresets,
            loadMorePresets,
            composer,
            regenerateMessage,
            getAssistantBranchCount,
            threadMessages,
            changeMessageBranch,
            resolvedSessionId,
            handleStopGeneration,
            isStopping,
            isTyping,
            streamingMessageId,
            streamingPhase,
            stickyContextRef,
            contextUsage,
        ]
    );

    const placeholderState: CommandProtoState = {
        presets: [],
        projects: [],
        sessions: [],
        agents: [],
        activeSessionId: null,
    };

    return (
        <CommandPrototypeContext.Provider
            value={{
                state: placeholderState,
                dispatch: () => undefined,
                stateRef,
                hydrated: true,
                apiStore,
            }}
        >
            {children}
            <CommandV2AgentSwitchDialog
                prompt={agentSwitchPrompt}
                onClose={() => setAgentSwitchPrompt(null)}
                onSwitch={() => {
                    if (!agentSwitchPrompt) return;
                    applyChatSelection(
                        agentSwitchPrompt.sessionId,
                        agentSwitchPrompt.sessionAgentId,
                        agentSwitchPrompt.projectId
                    );
                    setAgentSwitchPrompt(null);
                }}
                onStay={() => {
                    if (!agentSwitchPrompt) return;
                    applyChatSelection(
                        agentSwitchPrompt.sessionId,
                        agentSwitchPrompt.currentAgentId,
                        agentSwitchPrompt.projectId
                    );
                    setAgentSwitchPrompt(null);
                }}
            />
        </CommandPrototypeContext.Provider>
    );
}
